import { useAuthor } from '@/hooks/useAuthor';
import { avatarFallback, shortPubkey } from '@/lib/format';
import { cn } from '@/lib/utils';

export function displayNameOf(metadata: { name?: string; display_name?: string } | undefined, pubkey: string): string {
  return metadata?.display_name || metadata?.name || shortPubkey(pubkey);
}

interface AvatarProps {
  pubkey: string;
  src?: string;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizes: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'size-6 text-[10px]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
  xl: 'size-20 text-2xl',
};

/** Profile picture with deterministic initials fallback (no external service). */
export function Avatar({ pubkey, src, alt, size = 'md', className }: AvatarProps) {
  const { hue, initials } = avatarFallback(pubkey);
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-muted-foreground',
        sizes[size],
        className,
      )}
      title={alt}
      aria-label={alt}
      role="img"
    >
      {!src ? (
        <span
          aria-hidden
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, hsl(${hue} 70% 88%), hsl(${(hue + 40) % 360} 70% 78%))` }}
        />
      ) : null}
      {!src ? <span className="relative">{initials}</span> : null}
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : null}
    </span>
  );
}

/** Avatar wired to a pubkey's kind 0 profile. */
export function ProfileAvatar({ pubkey, size = 'md', className }: { pubkey: string; size?: AvatarProps['size']; className?: string }) {
  const { data } = useAuthor(pubkey);
  const metadata = data?.metadata;
  return (
    <Avatar
      pubkey={pubkey}
      src={metadata?.picture}
      alt={displayNameOf(metadata, pubkey)}
      size={size}
      className={className}
    />
  );
}
